/**
 * Метаинформация о комментариях, привязанных к сущности.
 *
 * Подмешивается бэком только когда реестр запрошен с requireCommentsInfo=true.
 * Если статус есть — qty/qtyRead/isUnread всегда заполнены.
 *
 * lastComment приходит только если бэк запросил с lastComment=true и
 * сохраняется как сырой объект — гидрация в Comment должна делаться
 * потребителем (Comment.fromServer(status.lastComment)). Сделано так
 * чтобы избежать циклической зависимости BaseEntity → CommentsStatus → Comment → BaseEntity.
 *
 * Методы register* поддерживают локальные счётчики в актуальном состоянии
 * после оптимистичных операций. Серверная истина живёт в comments.comments_count
 * и comments.comments_reads_count и подтянется при следующем рефреше.
 */
class CommentsStatus {

	qty:         number  = 0;
	qtyRead:     number  = 0;
	isUnread:    boolean = false;

	/**
	 * Сырой объект последнего коммента от бэка. Намеренно НЕ типизирован как Comment
	 * и НЕ гидрируется здесь, чтобы избежать цикла BaseEntity → CommentsStatus → Comment → BaseEntity.
	 * Если потребителю нужен типизированный Comment — гидрировать самому
	 * через Comment.fromServer(status.lastComment).
	 *
	 * Тип unknown (а не any) намеренно — заставляет потребителя явно кастить и не позволяет
	 * случайно обращаться к полям как к Comment без приведения.
	 */
	lastComment: unknown = null;

	static fromServer(data: any, result = new CommentsStatus()): CommentsStatus {
		result.qty         = data.qty ?? 0;
		result.qtyRead     = data.qtyRead ?? 0;
		result.isUnread    = data.isUnread ?? false;
		result.lastComment = data.lastComment ?? null;

		return result;
	}

	/**
	 * Зарегистрировать собственное добавление коммента.
	 * Автор сразу прочитал свой коммент — потому qtyRead тоже растёт.
	 */
	registerOwnAdd(): void {
		this.qty     += 1;
		this.qtyRead += 1;
		this.recalcIsUnread();
	}

	/**
	 * Зарегистрировать удаление коммента.
	 * На фронте удалять может только автор (бэк это проверяет),
	 * а автор всегда уже прочитал свой коммент — потому уменьшаем оба счётчика.
	 */
	registerDelete(): void {
		this.qty     = Math.max(0, this.qty     - 1);
		this.qtyRead = Math.max(0, this.qtyRead - 1);
		this.recalcIsUnread();
	}

	/**
	 * Пометить все комменты прочитанными.
	 */
	markAllRead(): void {
		this.qtyRead = this.qty;
		this.recalcIsUnread();
	}

	private recalcIsUnread(): void {
		this.isUnread = this.qty > this.qtyRead;
	}
}

export default CommentsStatus;